import { applicantHttpService, caseHttpService, filesHttpService } from "@myinnsworth/httpServices";
import { IEnumeratedFolder } from "@myinnsworth/models/storage/IEnumeratedFolder";
import { IStoredItem, StorageItemType } from "@myinnsworth/models/storage/IStoredItem";
import Icon from "components/icon/icon";
import Table, { ITableHeader, ITableRow } from "components/table/table";
import { format } from "date-fns";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

export interface IFileManagerProps {
    caseSlug?: string;
    institutionId?: number;
    fundId?: number;
    includeCaseName?: boolean;
}

export default function FileManager(props: IFileManagerProps) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [headers, setHeaders] = useState<Array<ITableHeader>>([]);
    const [rows, setRows] = useState<Array<ITableRow>>([]);
    const [fileSourceUrl, setFileSourceUrl] = useState<string | null>(null);
    const [pageNo, setPageNo] = useState<number>(1);
    const [totalResults, setTotalResults] = useState<number>(0);

    const onView = useCallback(async (item: IStoredItem) => {
        const url = await filesHttpService.getViewUrl(item);
        window.open(url, '_blank');
    }, []);

    const onDownload = useCallback(async (item: IStoredItem) => {
        const url = await filesHttpService.getDownloadUrl(item);
        setFileSourceUrl(url);
        setTimeout(() => { setFileSourceUrl(null) }, 750);
    }, []);

    useEffect(() => {
        const tableHeaders = new Array<ITableHeader>();
        tableHeaders.push({ name: '', width: '40px' });
        tableHeaders.push({ name: 'Name' });
        if (props.includeCaseName) {
            tableHeaders.push({ name: 'Case' });
        }
        tableHeaders.push({ name: 'Published date', width: '220px' });
        tableHeaders.push({ name: 'Action', width: '110px' });
        setHeaders(tableHeaders);
    }, [props]);

    const enumerateFolder = useCallback(async () => {
        let result: IEnumeratedFolder | null = null;
        const currentFolder = searchParams.get('folder') || '';
        const isLatest = !props.caseSlug && !props.institutionId && !props.fundId;
        const isCase = props.caseSlug && !props.institutionId && !props.fundId;
        const isInstitution = props.caseSlug && props.institutionId && !props.fundId;
        const isFund = props.caseSlug && props.institutionId && props.fundId;

        if (isLatest) {
            result = await filesHttpService.fetchLatestUploads(25);
        } else if(isCase) {
            result = await caseHttpService.enumerateFolder(props.caseSlug!, currentFolder);
        } else if (isInstitution) {
            result = await applicantHttpService.enumerateInstitutionFolder(props.caseSlug!, props.institutionId!, currentFolder);
        } else if (isFund) {
            result = await applicantHttpService.enumerateFundFolder(props.caseSlug!, props.institutionId!, props.fundId!, currentFolder);
        }

        if (result) {
            setTotalResults(result.items.length);
            let rows = result.items.map(item => {
                const cells = new Array<ReactNode>();
                if(item.type === StorageItemType.File) {
                    cells.push(<Icon name="envelope" />);
                } else {
                    cells.push(<></>);
                }
                if(item.type === StorageItemType.File) {
                    cells.push(<>                                    
                        <Icon name="file" margin="0 10px 0 0" color="#262824" />
                        {item.fileName}
                    </>);
                } else {
                    cells.push(<>
                        <Icon name="folder" margin="0 10px 0 0" color="#262824" />
                        {item.folderName}
                    </>);
                }
                if (props.includeCaseName) {
                    cells.push(item.case.name);
                }
                cells.push(format(item.created, 'dd/MM/yyyy'));
                if (item.type === StorageItemType.File) {
                    cells.push(<>
                        <Icon name="view" width="20px" height="16px" pointer onClick={() => onView(item)} />
                        <Icon name="cloud" width="20px" height="16px" pointer margin="0 0 0 15px" onClick={() => onDownload(item)} />
                    </>);
                } else {
                    let folderPath = '';
                    if(currentFolder) {
                        folderPath = `${currentFolder}/${item.folderName!}`;
                    } else {
                        folderPath = item.folderName!;
                    }
                    cells.push(<>
                        <a style={{cursor: 'pointer'}} onClick={() => setSearchParams({folder: folderPath})}>View</a>
                    </>);
                }


                return {cells};
            });

            //If we have a current folder, then we need to show the [..] (up a folder) folder
            if(currentFolder) {
                const previousFolder = currentFolder.includes('/') ? 
                    currentFolder.substring(0, currentFolder.indexOf('/')) : 
                    ''
                const cells = new Array<ReactNode>();
                cells.push(<></>);
                cells.push(<><Icon name="folder" margin="0 10px 0 0" color="#262824" />[..]</>);
                if(props.includeCaseName) {
                    cells.push(<></>);
                }
                cells.push(<></>);
                if(previousFolder) {
                    cells.push(<a style={{cursor: 'pointer'}} onClick={() => setSearchParams({folder: previousFolder})}>View</a>);
                } else {
                    cells.push(<a style={{cursor: 'pointer'}} onClick={() => setSearchParams({folder: ''})}>View</a>);
                }

                const previousFolderRow: ITableRow = {
                    cells: cells
                };

                rows = [
                    previousFolderRow,
                    ...rows
                ];
            }

            setRows(rows);
        }
    }, [searchParams, props, onView, onDownload, setSearchParams]);

    useEffect(() => {
        enumerateFolder();
    }, [enumerateFolder]);

    useEffect(() => {
        const onRefreshFiles = () => {
            enumerateFolder();
        };
        window.addEventListener('refresh-files', onRefreshFiles);
        return () => {
            window.removeEventListener('refresh-files', onRefreshFiles);
        };
    }, [enumerateFolder]);

    return (<>
        <Table
            nameIndex={1}
            actionIndex={headers.length - 1}
            hiddenIndices={[0]}
            headers={headers}
            rows={rows}
            allowPaging={true}
            currentPage={pageNo}
            totalResults={totalResults}
            resultsPerPage={totalResults} />
        {fileSourceUrl && (<iframe title="File Downloader" style={{ display: 'none' }} src={fileSourceUrl}></iframe>)}
    </>);
}